import { atwaterKcal, type FoodUnit } from '@gramas/shared'
import { Button, Field, NumberField, Segmented, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { api } from '../../api/client.js'
import type { LocalFood } from '../../db/dexie.js'
import { cacheFood, saveFood } from '../../db/repo.js'
import { sassLine } from '../../sass/engine.js'
import { syncNow } from '../../sync/engine.js'

export interface ManualFoodSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Present when correcting an existing product rather than creating one. */
  food?: LocalFood | null
  onSaved?: (food: LocalFood) => void
}

interface Values {
  name: string
  brand: string
  kcal: number | null
  protein: number | null
  carbs: number | null
  fat: number | null
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
  servingSizeG: number | null
  pieceWeightG: number | null
  unit: FoodUnit
}

const EMPTY: Values = {
  name: '',
  brand: '',
  kcal: null,
  protein: null,
  carbs: null,
  fat: null,
  sugars: null,
  satFat: null,
  fiber: null,
  salt: null,
  servingSizeG: null,
  pieceWeightG: null,
  unit: 'g',
}

/**
 * Hand-entered product, and the only way to correct a wrong one.
 *
 * A correction goes to the server rather than staying local, because the
 * catalogue is shared and the merge job ranks claims: a hand edit is recorded
 * at the highest precedence there is, so tonight's scrape cannot quietly put
 * the wrong number back.
 */
export function ManualFoodSheet({ open, onOpenChange, food, onSaved }: ManualFoodSheetProps) {
  const [values, setValues] = useState<Values>(EMPTY)
  /** What was loaded when the sheet opened — the thing "unsaved" is measured against. */
  const [baseline, setBaseline] = useState<Values>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    const loaded: Values = food
      ? {
          name: food.nameLt ?? food.nameEn ?? '',
          brand: food.brand ?? '',
          kcal: food.kcal,
          protein: food.protein,
          carbs: food.carbs,
          fat: food.fat,
          sugars: food.sugars,
          satFat: food.satFat,
          fiber: food.fiber,
          salt: food.salt,
          servingSizeG: food.servingSizeG,
          pieceWeightG: food.pieceWeightG ?? null,
          unit: food.unit,
        }
      : EMPTY
    setValues(loaded)
    setBaseline(loaded)
  }, [open, food])

  const dirty = JSON.stringify(values) !== JSON.stringify(baseline)

  const set = <K extends keyof Values>(key: K, value: Values[K]) =>
    setValues((v) => ({ ...v, [key]: value }))

  const complete =
    values.name.trim().length > 0 &&
    values.kcal != null &&
    values.protein != null &&
    values.carbs != null &&
    values.fat != null

  const nutrients = {
    kcal: values.kcal ?? 0,
    protein: values.protein ?? 0,
    carbs: values.carbs ?? 0,
    fat: values.fat ?? 0,
    sugars: values.sugars,
    satFat: values.satFat,
    fiber: values.fiber,
    salt: values.salt,
  }

  /**
   * The energy the macros add up to, against the energy that was typed.
   *
   * Every other way into the catalogue is cross-checked this way — the OCR
   * review sheet and the scraper merge both do it — and this is the one place a
   * person types all five numbers by hand, which is exactly where a kJ figure
   * pasted into the kcal box or a missed decimal comma gets in. It warns and
   * lets the save through: labels really are allowed to disagree with their own
   * macros, and a nag that cannot be overruled is a nag that gets worked around.
   */
  const derivedKcal =
    values.protein != null && values.carbs != null && values.fat != null
      ? atwaterKcal({
          protein: values.protein,
          carbs: values.carbs,
          fat: values.fat,
          fiber: values.fiber,
        })
      : null
  const energyMismatch =
    derivedKcal != null && values.kcal != null && derivedKcal > 0
      ? Math.abs(values.kcal - derivedKcal) / derivedKcal > 0.3
      : false

  const save = async () => {
    if (!complete || saving) return
    setSaving(true)
    setError(null)
    try {
      if (food) {
        // Macros go as one object because the server files them as a single
        // provenance claim: half a nutrition label is not a statement about
        // the product, and the merge job ranks whole claims.
        const updated = await api.patch<{ food: LocalFood }>(`/api/foods/${food.id}`, {
          nameLt: values.name.trim(),
          brand: values.brand.trim() || null,
          servingSizeG: values.servingSizeG,
          pieceWeightG: values.pieceWeightG,
          unit: values.unit,
          per100g: nutrients,
        })
        await cacheFood({ ...food, ...updated.food })
        onOpenChange(false)
        onSaved?.({ ...food, ...updated.food })
        return
      }

      const created = await saveFood(
        {
          nameLt: values.name.trim(),
          brand: values.brand.trim() || null,
          servingSizeG: values.servingSizeG,
          pieceWeightG: values.pieceWeightG,
          unit: values.unit,
          visibility: 'public',
        },
        nutrients,
      )
      void syncNow()
      onOpenChange(false)
      onSaved?.(created)
    } catch {
      const line = sassLine('error_save', {})
      setError(
        line ??
          (food
            ? 'Nepavyko išsaugoti pataisymo — reikia ryšio su serveriu.'
            : 'Nepavyko išsaugoti produkto.'),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      dirty={dirty && !saving}
      title={food ? 'Taisyti produktą' : 'Naujas produktas'}
      description={`Reikšmės 100 ${values.unit} produkto`}
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          onClick={() => void save()}
          disabled={!complete || saving}
        >
          {saving ? 'Saugoma…' : 'Išsaugoti'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <Field
          label="Pavadinimas"
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          required
        />
        <Field
          label="Gamintojas"
          value={values.brand}
          onChange={(e) => set('brand', e.target.value)}
        />
        <Segmented
          label="Matas"
          value={values.unit}
          onValueChange={(next) => set('unit', next)}
          options={[
            { value: 'g', label: 'Gramai' },
            { value: 'ml', label: 'Mililitrai' },
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label="Energija"
            suffix="kcal"
            value={values.kcal}
            onChange={(v) => set('kcal', v)}
          />
          <NumberField
            label="Baltymai"
            suffix="g"
            value={values.protein}
            onChange={(v) => set('protein', v)}
          />
          <NumberField
            label="Angliavandeniai"
            suffix="g"
            value={values.carbs}
            onChange={(v) => set('carbs', v)}
          />
          <NumberField
            label="Riebalai"
            suffix="g"
            value={values.fat}
            onChange={(v) => set('fat', v)}
          />
          <NumberField
            label="iš kurių cukrūs"
            suffix="g"
            value={values.sugars}
            onChange={(v) => set('sugars', v)}
          />
          <NumberField
            label="Sočiosios r. r."
            suffix="g"
            value={values.satFat}
            onChange={(v) => set('satFat', v)}
          />
          <NumberField
            label="Skaidulos"
            suffix="g"
            value={values.fiber}
            onChange={(v) => set('fiber', v)}
          />
          <NumberField
            label="Druska"
            suffix="g"
            value={values.salt}
            onChange={(v) => set('salt', v)}
          />
        </div>
        {energyMismatch && derivedKcal != null && (
          <p className="text-sm text-warn">
            Pagal baltymus, angliavandenius ir riebalus išeitų apie {Math.round(derivedKcal)} kcal.
            Patikrinkite, ar nesuvelta su kJ.
          </p>
        )}
        <NumberField
          label="Porcijos dydis"
          suffix={values.unit}
          hint="Nebūtina — leidžia įrašyti „1 porciją“ vienu paspaudimu"
          value={values.servingSizeG}
          onChange={(v) => set('servingSizeG', v)}
        />
        {/*
          Not the same field as the serving above it. A serving is what the
          manufacturer suggests; this says the product is counted at all, and
          filling it turns every screen that asks "how much?" into one that asks
          "how many?".
        */}
        <NumberField
          label="Vieno vieneto svoris"
          suffix="g"
          hint="Pildykite tik tam, kas skaičiuojama, o ne sveriama — kiaušiniams, bandelėms"
          value={values.pieceWeightG}
          onChange={(v) => set('pieceWeightG', v)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}
